import type { ReactNode } from 'react';

import { Save, type LucideIcon } from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

interface CartaoDeConfiguracaoProps {
  /** Título e descrição da página inteira. */
  titulo: string;
  descricao: string;
  /** Título do cartão que envolve os campos, com o ícone do assunto. */
  tituloDoCartao: string;
  icone: LucideIcon;
  carregando: boolean;
  comErro: boolean;
  /** Mensagem do erro de carga, em português e sem detalhe técnico. */
  mensagemDeErro: string;
  aoTentarDeNovo: () => void;
  /** Valida antes de "salvar". Devolver falso cancela — a mensagem é sua. */
  aoValidar?: () => boolean;
  /** Botão extra à esquerda do "Salvar", tipicamente "Restaurar padrão". */
  acaoSecundaria?: ReactNode;
  larguraMaxima?: string;
  children: ReactNode;
}

/**
 * O esqueleto compartilhado das telas de configuração de valor único.
 *
 * Prazo de fechamento, alerta de desvio e parâmetros do ateste são a mesma tela
 * com campos diferentes: cabeçalho, um cartão, os quatro estados e um "Salvar"
 * no rodapé. Escrever as três à mão repetiria a estrutura três vezes, e duas
 * repetições já pedem extração (CLAUDE.md, "Regras de interface", item 1).
 *
 * O "SALVAR" NUNCA GRAVA. Nenhuma das telas de configuração tem rota de escrita
 * na API — a tabela `configuracoes` existe no banco desde a migração
 * `0002_dominio_base.sql`, mas sem política de escrita e sem endpoint. O botão é
 * renderizado, valida o que o usuário digitou, e então mostra o aviso honesto de
 * indisponibilidade em vez de fingir que gravou
 * (`modulos/operacao/comum/cadastroIndisponivel.ts`).
 *
 * Validar antes de avisar não é teatro: é a validação que a tela vai precisar
 * quando o endpoint existir, e mantê-la agora impede que a interface aceite hoje
 * o que o servidor recusará depois.
 */
export function CartaoDeConfiguracao({
  titulo,
  descricao,
  tituloDoCartao,
  icone: Icone,
  carregando,
  comErro,
  mensagemDeErro,
  aoTentarDeNovo,
  aoValidar,
  acaoSecundaria,
  larguraMaxima = 'max-w-xl',
  children,
}: CartaoDeConfiguracaoProps) {
  function salvar() {
    if (aoValidar && !aoValidar()) {
      return;
    }
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <div className="space-y-6">
      <PageHeader title={titulo} description={descricao} />

      <Card className={larguraMaxima}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Icone className="h-4 w-4 text-primary" aria-hidden="true" />
            {tituloDoCartao}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {comErro ? (
            <ErrorState
              variant="load-failed"
              description={mensagemDeErro}
              primaryAction={{ label: 'Tentar de novo', onClick: aoTentarDeNovo }}
            />
          ) : carregando ? (
            <Spinner />
          ) : (
            <>
              {children}
              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <div>{acaoSecundaria}</div>
                <Button onClick={salvar}>
                  <Save className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Salvar
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
